import { ToastType } from '@components/Toast/Toast';
import {
  fetchCustomerInfo,
  fetchEdiLogs,
  fetchEdiSettingsValueData,
  fetchFeatureFlagGetCustomerFeatures,
  getCustomerContact,
} from '@store/orders/services/orderCustomerServices';
import {
  orderStore,
  setCustomerData,
  setCustomerLoading,
  setEdiLogsData,
  setEdiLogsStore,
  setEdiSettingsValuesData,
  setEdiSettingsValuesError,
  setEdiSettingsValuesLoading,
  setFeatureFlagGetCustomerFeaturesData,
  setFeatureFlagGetCustomerFeaturesError,
  setFeatureFlagGetCustomerFeaturesLoading,
} from '@store/orders/store';
import { ICustomerContact } from '@typeDefinitions/contactTypes';
import { logError } from '@utils/errorLogging';
import { handleToast } from '@utils/utils';

import { updateOrderState } from './orderFormActions';

export const loadCustomerInfo = async (customerId: number) => {
  try {
    setCustomerLoading(true);

    const customerInfo = await fetchCustomerInfo(customerId);
    await loadEdiSettingsValueData(customerId);
    await loadFeatureFlagGetCustomerFeatures(customerId);
    setCustomerData(customerInfo);

    if (
      Boolean(customerInfo.assignAccountManagerAsOrderRep) &&
      (!orderStore.order.assignedId || orderStore.isCreate)
    ) {
      updateOrderState({
        owner: customerInfo.accountManager,
        assignedId: customerInfo.accountManagerId,
      });
    }

    setCustomerLoading(false);
    return customerInfo;
  } catch (error: unknown) {
    logError('loadCustomerInfo', error as Error);
    setCustomerLoading(false);
  }
};

export const loadEdiSettingsValueData = async (customerId: number) => {
  setEdiSettingsValuesLoading(true);
  try {
    const response = await fetchEdiSettingsValueData(customerId);
    setEdiSettingsValuesData(response);
  } catch (error) {
    setEdiSettingsValuesError(true);
  } finally {
    setEdiSettingsValuesLoading(false);
  }
};

export const loadFeatureFlagGetCustomerFeatures = async (
  customerId: number,
) => {
  setFeatureFlagGetCustomerFeaturesLoading(true);
  try {
    const response = await fetchFeatureFlagGetCustomerFeatures(customerId);
    setFeatureFlagGetCustomerFeaturesData(response);
  } catch (error) {
    setFeatureFlagGetCustomerFeaturesError(true);
  } finally {
    setFeatureFlagGetCustomerFeaturesLoading(false);
  }
};

export const loadCustomerEdiLogs = async (
  customerId: number,
  shipmentId: string | undefined,
) => {
  setEdiLogsStore(() => ({
    loading: true,
  }));
  try {
    const ediTransactionHistory = await fetchEdiLogs(customerId, shipmentId);

    setEdiLogsData(ediTransactionHistory);
  } catch (error) {
    logError('loadCustomerEdiLogs', error as Error);
    setEdiLogsStore(() => ({ isError: true }));
    handleToast(ToastType.Error, (error as Error).message);
  } finally {
    setEdiLogsStore(() => ({
      loading: false,
    }));
  }
};

export const fetchCustomerContact = async (custumerId: number) => {
  try {
    const response: ICustomerContact[] | undefined =
      await getCustomerContact(custumerId);
    if (Array.isArray(response) && response.length > 0) {
      return response;
    }
  } catch (error) {
    if (error instanceof Error) {
      handleToast(ToastType.Error, 'Failed to get Customer Contact');
      throw new Error(`Failed to Customer Contact: ${error.message}`);
    }

    logError('fetchCustomerContact', error as Error);
  }
};
